import React, { useEffect, useRef } from "react";
import { createChart, ColorType, AreaSeries, LineSeries } from "lightweight-charts";
import { useTheme } from "../context/ThemeContext";

// Same palette pair as TradingChart — kept locally so each chart can be
// re-skinned independently if product asks for accent differentiation later.
const CHART_THEMES = {
  light: { background: "#FFFFFF", textColor: "#6B7068", grid: "#F0EDE8", crosshair: "#547A8F" },
  dark: { background: "#161B22", textColor: "#B8C0CC", grid: "#1F2630", crosshair: "#3FB962" },
};

/**
 * Area chart of paper-trading equity, with optional benchmark (e.g. SPY) line overlay.
 * snapshots: [{ timestamp, total_value, ... }]
 * benchmark: { symbol, name, points: [{ timestamp, value }] } | null
 */
export default function PortfolioChart({
  snapshots = [],
  benchmark = null,
  trendUp = true,
  height = 240,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const equityRef = useRef(null);
  const benchRef = useRef(null);
  const { resolved } = useTheme();
  const tokens = CHART_THEMES[resolved] || CHART_THEMES.light;

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: tokens.background },
        textColor: tokens.textColor,
        fontFamily: "IBM Plex Mono, monospace",
        fontSize: 10,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: tokens.grid },
      },
      rightPriceScale: { borderColor: "transparent", scaleMargins: { top: 0.15, bottom: 0.05 } },
      timeScale: { borderColor: "transparent", timeVisible: false, secondsVisible: false },
      crosshair: {
        mode: 1,
        horzLine: { color: tokens.crosshair, style: 2, width: 1 },
        vertLine: { color: tokens.crosshair, style: 2, width: 1 },
      },
      handleScroll: false,
      handleScale: false,
      localization: { locale: "en-US" },
      height,
      autoSize: true,
    });
    chartRef.current = chart;
    const ro = new ResizeObserver(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
      }
    });
    ro.observe(containerRef.current);
    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      equityRef.current = null;
      benchRef.current = null;
    };
  }, [height]);

  // Hot-swap canvas colors on theme flip.
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      layout: {
        background: { type: ColorType.Solid, color: tokens.background },
        textColor: tokens.textColor,
      },
      grid: { horzLines: { color: tokens.grid } },
      crosshair: {
        horzLine: { color: tokens.crosshair },
        vertLine: { color: tokens.crosshair },
      },
    });
  }, [tokens]);

  // Equity series
  useEffect(() => {
    if (!chartRef.current) return;
    const chart = chartRef.current;
    if (equityRef.current) {
      try { chart.removeSeries(equityRef.current); } catch (e) { console.warn("PortfolioChart: equity series cleanup failed", e); }
      equityRef.current = null;
    }
    if (!snapshots || snapshots.length === 0) return;

    const trendColor = trendUp ? "#5C8D73" : "#C66B59";
    const series = chart.addSeries(AreaSeries, {
      lineColor: trendColor,
      topColor: `${trendColor}40`,
      bottomColor: `${trendColor}05`,
      lineWidth: 2,
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
      title: "Portfolio",
    });
    const seen = new Map();
    for (const s of snapshots) {
      const t = Math.floor(new Date(s.timestamp).getTime() / 1000);
      seen.set(t, s.total_value);
    }
    const data = [...seen.entries()].sort((a, b) => a[0] - b[0])
      .map(([t, v]) => ({ time: t, value: v }));
    series.setData(data);
    equityRef.current = series;
    chart.timeScale().fitContent();
  }, [snapshots, trendUp]);

  // Benchmark overlay
  useEffect(() => {
    if (!chartRef.current) return;
    const chart = chartRef.current;
    if (benchRef.current) {
      try { chart.removeSeries(benchRef.current); } catch (e) { console.warn("PortfolioChart: benchmark series cleanup failed", e); }
      benchRef.current = null;
    }
    if (!benchmark || !benchmark.points || benchmark.points.length === 0) return;
    if (!snapshots || snapshots.length === 0) return;

    // Clip benchmark to portfolio time range
    const firstSnap = Math.floor(new Date(snapshots[0].timestamp).getTime() / 1000);
    const series = chart.addSeries(LineSeries, {
      color: "#547A8F",
      lineWidth: 2,
      lineStyle: 2, // dashed
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
      title: benchmark.symbol,
    });
    const seen = new Map();
    for (const p of benchmark.points) {
      const t = Math.floor(new Date(p.timestamp).getTime() / 1000);
      if (t < firstSnap) continue;
      seen.set(t, p.value);
    }
    const data = [...seen.entries()].sort((a, b) => a[0] - b[0])
      .map(([t, v]) => ({ time: t, value: v }));
    if (data.length === 0) return;
    series.setData(data);
    benchRef.current = series;
  }, [benchmark, snapshots]);

  return (
    <div
      ref={containerRef}
      data-testid="portfolio-chart"
      style={{ width: "100%", height }}
    />
  );
}
